<template>
  <section class="benefits-banners">
    <header class="benefits-banners-header">
      <h3>{{ t("benefitsCenter-banners-title") }}</h3>

      <span>
        {{ t("benefitsCenter-banners-count", [normalizedBanners.length, maxBanners]) }}
      </span>
    </header>

    <BenefitsFileUpload accept="image/jpeg,image/png" multiple :disabled="isLimitReached"
      :title="isLimitReached ? t('benefitsCenter-banners-limitReachedTitle') : t('benefitsCenter-banners-dropTitle')"
      :description="isLimitReached ? t('benefitsCenter-banners-limitReachedDescription') : t('benefitsCenter-banners-dropDescription')"
      :button-label="isLimitReached ? '' : t('benefitsCenter-banners-searchFile')" @files="addFiles" />

    <draggable v-if="normalizedBanners.length" :value="normalizedBanners" class="benefits-banners-list" :options="bannerDragOptions"
      @input="updateBannerOrder">
      <BenefitsBannerItem v-for="(banner, index) in normalizedBanners" :key="banner.id"
        :banner="banner" :index="index" @delete="deleteBanner" @edit-link="openLinkModal" />
    </draggable>

    <div v-if="linkModalOpen" class="benefits-banner-modal-backdrop" @click.self="closeLinkModal">
      <KvCard class="benefits-link-modal" content-class="benefits-link-modal-content">
        <template #text>
          <form @submit.prevent="saveBannerLink">
            <h4>{{ t("benefitsCenter-banners-linkModalTitle") }}</h4>

            <div class="benefits-link-modal-row">
              <label class="benefits-link-field" for="benefits-banner-link">
                <span>{{ t("benefitsCenter-banners-linkLabel") }}</span>

                <KvInput
                  id="benefits-banner-link"
                  v-model="linkDraft"
                  variant="outlined"
                  size="large"
                  autocomplete="off"
                  placeholder="https://"
                />
              </label>

              <KvButton class="benefits-link-copy-button" type="button" variant="outlined" @click="copyBannerLink">
                <Icon :icon-class="copiedLink ? 'ui-check' : 'ui-copy-03'" size="4" color="#1f2a37" />
                {{ copyButtonLabel }}
              </KvButton>
            </div>

            <div class="benefits-link-modal-footer">
              <KvButton class="benefits-link-close-button" type="submit" variant="outlined">
                {{ t("benefitsCenter-banners-linkModalSave") }}
              </KvButton>
            </div>
          </form>
        </template>
      </KvCard>
    </div>

    <div v-if="deleteConfirmOpen" class="benefits-banner-modal-backdrop" @click.self="closeDeleteConfirm">
      <KvCard class="benefits-delete-modal" content-class="benefits-delete-modal-content">
        <template #text>
          <h4>{{ t("benefitsCenter-banners-deleteModalTitle") }}</h4>
          <p>{{ t("benefitsCenter-banners-deleteModalDescription") }}</p>

          <div class="benefits-delete-modal-actions">
            <KvButton class="benefits-link-close-button" type="button" variant="outlined" @click="closeDeleteConfirm">
              {{ t("benefitsCenter-banners-deleteModalCancel") }}
            </KvButton>

            <KvButton class="benefits-delete-confirm-button" type="button" variant="outlined"
              @click="confirmDeleteBanner">
              {{ t("benefitsCenter-banners-deleteModalConfirm") }}
            </KvButton>
          </div>
        </template>
      </KvCard>
    </div>
  </section>
</template>

<script>
import draggable from "vuedraggable";
import Icon from "../../../components/icon/icon";
import KvButton from "../../../components/form/kv-button.vue";
import KvCard from "../../../components/ui/kv-card.vue";
import KvInput from "../../../components/inputs/kv-input.vue";
import BenefitsBannerItem from "./benefits-banner-item.vue";
import BenefitsFileUpload from "../../shared/benefits-file-upload.vue";
import { uploadFilesToStorage } from "../../shared/upload-helper";

export default {
  name: "BenefitsBanners",
  components: {
    draggable,
    Icon,
    KvButton,
    KvCard,
    KvInput,
    BenefitsBannerItem,
    BenefitsFileUpload,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    maxBanners: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      copiedLink: false,
      hasCopiedLink: false,
      copyResetTimer: null,
      deleteConfirmOpen: false,
      editingLinkIndex: null,
      pendingDeleteIndex: null,
      linkDraft: "",
      linkModalOpen: false,
      uploadStatuses: {},
      localBanners: [],
    };
  },
  computed: {
    bannerDragOptions() {
      return {
        animation: 180,
        easing: "cubic-bezier(0.2, 0, 0, 1)",
        ghostClass: "benefits-banner-sort-ghost",
        chosenClass: "benefits-banner-sort-chosen",
        dragClass: "benefits-banner-sort-drag",
      };
    },
    normalizedBanners() {
      const all = [...this.value, ...this.localBanners];
      return all.map(this.normalizeBanner);
    },
    isLimitReached() {
      return this.normalizedBanners.length >= this.maxBanners;
    },
    copyButtonLabel() {
      if (this.copiedLink) return this.t("benefitsCenter-banners-linkCopied");

      return this.t(
        this.hasCopiedLink
          ? "benefitsCenter-banners-copyLinkAgain"
          : "benefitsCenter-banners-copyLink"
      );
    },
  },
  beforeDestroy() {
    this.localBanners.forEach((banner) => {
      if (banner.previewUrl && banner.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(banner.previewUrl);
      }
    });
    clearTimeout(this.copyResetTimer);
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    async addFiles(fileList) {
      if (!fileList || this.isLimitReached) return;

      const remainingSlots = this.maxBanners - this.normalizedBanners.length;
      const files = Array.from(fileList)
        .filter(this.isAcceptedImage)
        .slice(0, remainingSlots);

      if (!files.length) return;

      const nextBanners = files.map((file) => {
        const previewUrl = URL.createObjectURL(file);
        const id = `local-${Date.now()}-${file.name}-${Math.random().toString(36).substr(2, 5)}`;

        this.$set(this.uploadStatuses, id, "uploading");

        return {
          id,
          name: file.name,
          previewUrl,
          isLocal: true,
        };
      });

      this.localBanners.push(...nextBanners);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const tempId = nextBanners[i].id;

        try {
          const urls = await uploadFilesToStorage([file], this.$root.t.bind(this.$root));

          if (urls && urls.length > 0) {
            this.$set(this.uploadStatuses, tempId, "completed");
            this.localBanners = this.localBanners.filter((banner) => banner.id !== tempId);
            this.$emit("input", [
              ...this.value,
              { id: tempId, name: file.name, previewUrl: urls[0], url: urls[0] }
            ]);
            this.$store.dispatch("notification/success", {
              text: this.t("benefitsCenter-upload-success"),
            });
          }
        } catch (error) {
          const failedBanner = this.localBanners.find((banner) => banner.id === tempId);
          if (failedBanner && failedBanner.previewUrl && failedBanner.previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(failedBanner.previewUrl);
          }
          this.localBanners = this.localBanners.filter((banner) => banner.id !== tempId);
          this.$delete(this.uploadStatuses, tempId);
          
          this.$store.dispatch("notification/error", { 
            text: error.message || this.t("benefitsCenter-upload-uploadError")
          });
        }
      }
    },
    isAcceptedImage(file) {
      const acceptedTypes = ["image/jpeg", "image/jpg", "image/png"];
      const acceptedExtensions = [".jpg", ".jpeg", ".png"];
      const fileName = String(file.name || "").toLowerCase();

      return acceptedTypes.includes(file.type) || acceptedExtensions.some((extension) => fileName.endsWith(extension));
    },
    normalizeBanner(banner, index) {
      const id = this.getBannerId(banner, index);
      return {
        ...banner,
        id,
        link: banner.link || banner.redirectUrl || "",
        name: banner.name || "",
        previewUrl: banner.previewUrl || banner.url || "",
        status: this.uploadStatuses[id] || null,
      };
    },
    getBannerId(banner, index) {
      return banner.id || `banner-${index}`;
    },
    updateBannerOrder(nextBanners) {
      const newSaved = [];
      const newLocal = [];
      nextBanners.forEach((banner) => {
        if (banner.isLocal) {
          newLocal.push(banner);
        } else {
          newSaved.push(banner);
        }
      });
      this.localBanners = newLocal;
      this.$emit("input", newSaved);
    },
    openLinkModal(index) {
      const banner = this.normalizeBanner(this.value[index] || {}, index);

      this.editingLinkIndex = index;
      this.linkDraft = banner.link;
      this.copiedLink = false;
      this.hasCopiedLink = false;
      this.linkModalOpen = true;
    },
    closeLinkModal() {
      this.saveBannerLink();
    },
    saveBannerLink() {
      if (this.editingLinkIndex === null) {
        this.linkModalOpen = false;
        return;
      }

      const nextBanners = this.value.map((banner, index) => {
        if (index !== this.editingLinkIndex) return banner;

        let formattedLink = this.linkDraft.trim();
        if (formattedLink && !/^https?:\/\//i.test(formattedLink)) {
          formattedLink = `https://${formattedLink}`;
        }

        return {
          ...banner,
          link: formattedLink,
        };
      });

      this.$emit("input", nextBanners);
      this.linkModalOpen = false;
      this.editingLinkIndex = null;
      this.linkDraft = "";
      this.copiedLink = false;
      this.hasCopiedLink = false;
    },
    copyBannerLink() {
      const link = this.linkDraft.trim();

      if (!link) return;

      clearTimeout(this.copyResetTimer);

      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(link);
      }

      this.copiedLink = true;
      this.hasCopiedLink = true;
      this.copyResetTimer = setTimeout(() => {
        this.copiedLink = false;
      }, 3000);
    },
    deleteBanner(index) {
      this.pendingDeleteIndex = index;
      this.deleteConfirmOpen = true;
    },
    closeDeleteConfirm() {
      this.pendingDeleteIndex = null;
      this.deleteConfirmOpen = false;
    },
    confirmDeleteBanner() {
      if (this.pendingDeleteIndex === null) return;

      const indexToDelete = this.pendingDeleteIndex;
      const targetBanner = this.normalizedBanners[indexToDelete];

      if (targetBanner.isLocal) {
        this.localBanners = this.localBanners.filter((banner) => banner.id !== targetBanner.id);
      } else {
        this.$emit(
          "input",
          this.value.filter((banner) => banner.id !== targetBanner.id)
        );
      }

      this.closeDeleteConfirm();
      this.$store.dispatch("notification/success", {
        text: this.t("benefitsCenter-upload-bannerDeleted"),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits-banners {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.benefits-banners-header {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  width: 100%;

  h3 {
    color: #384250;
    font-family: "Sora", sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;
    margin: 0;
  }

  span {
    color: #6c737f;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;
  }
}

.benefits-banners-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.benefits-banners-list::v-deep .benefits-banner-sort-ghost {
  background: #ecfdf3;
  border-color: #2aaa4c;
  box-shadow: inset 0 0 0 2px rgba(42, 170, 76, 0.18);
  opacity: 0.9;
}

.benefits-banners-list::v-deep .benefits-banner-sort-ghost>* {
  opacity: 0;
}

.benefits-banners-list::v-deep .benefits-banner-sort-chosen {
  border-color: #2aaa4c;
  box-shadow: 0 8px 24px rgba(17, 25, 39, 0.14), 0 0 0 2px rgba(42, 170, 76, 0.12);
}

.benefits-banners-list::v-deep .benefits-banner-sort-drag {
  cursor: grabbing;
  opacity: 0.95;
}

.benefits-banner-modal-backdrop {
  align-items: center;
  background: rgba(17, 25, 39, 0.72);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 16px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1050;
}

.benefits-link-modal,
.benefits-delete-modal {
  max-width: 520px;
  width: 100%;
}

.benefits-link-modal::v-deep .kv-card-content-inner,
.benefits-delete-modal::v-deep .kv-card-content-inner {
  padding: 16px;
}

.benefits-link-modal form {
  display: flex;
  flex-direction: column;
  gap: 24px;

  h4 {
    color: #4d5761;
    font-family: "Sora", sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    margin: 0;
  }
}

.benefits-delete-modal h4 {
  color: #4d5761;
  font-family: "Sora", sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0 0 12px;
}

.benefits-delete-modal p {
  color: #384250;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  margin: 0;
}

.benefits-link-modal-row {
  align-items: flex-end;
  display: flex;
  gap: 8px;
  width: 100%;
}

.benefits-link-field {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  min-width: 0;

  span {
    color: #384250;
    font-family: "Sora", sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;
  }

  input {
    background: #f9fafb;
    border: 1px solid #1f2a37;
    border-radius: 8px;
    color: #1f2a37;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 400;
    height: 44px;
    line-height: 1.3;
    min-width: 0;
    outline: none;
    padding: 12px;
    width: 100%;

    &:focus {
      border-color: #2aaa4c;
    }
  }
}

.benefits-link-copy-button,
.benefits-link-close-button,
.benefits-delete-confirm-button {
  align-items: center;
  background: #fcfcfd;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #1f2a37;
  cursor: pointer;
  display: inline-flex;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  gap: 4px;
  height: 44px;
  justify-content: center;
  line-height: 1.2;
  padding: 12px;
  transition: background 0.15s ease;
  white-space: nowrap;

  &:hover,
  &:focus {
    background: #f9fafb;
    outline: none;
  }
}

.benefits-delete-confirm-button {
  background: #fee4e2;
  border-color: #fee4e2;
  color: #b42318;

  &:hover,
  &:focus {
    background: #fecdca;
  }
}

.benefits-link-copy-button::v-deep .kv-button-content,
.benefits-link-close-button::v-deep .kv-button-content,
.benefits-delete-confirm-button::v-deep .kv-button-content {
  gap: inherit;
  line-height: inherit;
}

.benefits-link-modal-footer {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.benefits-delete-modal-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 24px;
  width: 100%;
}


@media (max-width: 700px) {
  .benefits-banner-modal-backdrop {
    align-items: flex-start;
    overflow-y: auto;
  }

  .benefits-link-modal,
  .benefits-delete-modal {
    max-height: calc(100dvh - 32px);
    overflow-y: auto;
  }

  .benefits-link-modal-row {
    align-items: stretch;
    flex-direction: column;
  }

  .benefits-link-copy-button,
  .benefits-link-close-button,
  .benefits-delete-confirm-button {
    white-space: normal;
  }

  .benefits-link-copy-button {
    width: 100%;
  }

  .benefits-delete-modal-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }


}

@media (max-width: 480px) {
  .benefits-banners-header {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
